// Microphone capture for Guess: getUserMedia (echo cancellation on) → AudioWorklet frames.
// Falls back to ScriptProcessor on old browsers. Times are on the performance.now() clock.

import { asset } from "../../../lib/asset";
import { audioContext } from "../../../lib/sfx";

export type MicErrorCode = "denied" | "missing" | "insecure" | "unsupported";

export class MicError extends Error {
  constructor(public code: MicErrorCode) {
    super(code);
  }
}

type FrameFn = (frame: Float32Array, endAt: number, frameMs: number) => void;

export class Mic {
  private listeners = new Set<FrameFn>();
  private node: AudioWorkletNode | ScriptProcessorNode | null = null;
  private sink: GainNode | null = null;
  private closed = false;

  private constructor(
    readonly stream: MediaStream,
    readonly ctx: AudioContext,
    private source: MediaStreamAudioSourceNode,
  ) {}

  get sampleRate() {
    return this.ctx.sampleRate;
  }

  static async open(): Promise<Mic> {
    if (typeof window === "undefined") throw new MicError("unsupported");
    if (!window.isSecureContext) throw new MicError("insecure");
    if (!navigator.mediaDevices?.getUserMedia) throw new MicError("unsupported");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (e) {
      const name = (e as DOMException)?.name;
      throw new MicError(name === "NotAllowedError" || name === "SecurityError" ? "denied" : "missing");
    }
    const ctx = audioContext();
    if (!ctx) {
      stream.getTracks().forEach((t) => t.stop());
      throw new MicError("unsupported");
    }
    if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
    const mic = new Mic(stream, ctx, ctx.createMediaStreamSource(stream));
    await mic.attach();
    return mic;
  }

  private async attach() {
    const ctx = this.ctx;
    this.sink = ctx.createGain();
    this.sink.gain.value = 0; // keep the graph pulled without making a sound
    this.sink.connect(ctx.destination);
    try {
      if (!ctx.audioWorklet) throw new Error("no worklet");
      await ctx.audioWorklet.addModule(asset("worklets/hg-capture.js"));
      const node = new AudioWorkletNode(ctx, "hg-capture", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
      node.port.onmessage = (e: MessageEvent<{ t: number; f: Float32Array }>) => {
        const frameMs = (e.data.f.length / ctx.sampleRate) * 1000;
        this.emit(e.data.f, this.toPerf(e.data.t), frameMs);
      };
      this.node = node;
    } catch {
      const sp = ctx.createScriptProcessor(1024, 1, 1);
      sp.onaudioprocess = (e) => {
        const f = e.inputBuffer.getChannelData(0).slice();
        const frameMs = (f.length / ctx.sampleRate) * 1000;
        this.emit(f, this.toPerf(e.playbackTime || ctx.currentTime), frameMs);
      };
      this.node = sp;
    }
    this.source.connect(this.node);
    this.node.connect(this.sink);
  }

  /** Map an audio-clock time to performance.now(). */
  private toPerf(contextTime: number): number {
    try {
      const ts = this.ctx.getOutputTimestamp?.();
      if (ts && ts.contextTime !== undefined && ts.performanceTime !== undefined && ts.performanceTime > 0) {
        const t = ts.performanceTime + (contextTime - ts.contextTime) * 1000;
        const now = performance.now();
        // never in the future, never absurdly far in the past
        if (t <= now + 5 && t > now - 2000) return Math.min(t, now);
      }
    } catch {
      /* fall through */
    }
    return performance.now();
  }

  private emit(f: Float32Array, endAt: number, frameMs: number) {
    if (this.closed) return;
    for (const fn of this.listeners) fn(f, endAt, frameMs);
  }

  onFrame(fn: FrameFn): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.listeners.clear();
    try {
      this.source.disconnect();
      this.node?.disconnect();
      this.sink?.disconnect();
      if (this.node && "port" in this.node) this.node.port.onmessage = null;
    } catch {
      /* already gone */
    }
    this.stream.getTracks().forEach((t) => t.stop());
  }
}
